import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface VolunteerOpportunity {
  id: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  description_en: string;
  description_ps: string;
  description_da: string;
  commitment: string;
  location: string;
  category: string;
  is_active: boolean;
}

function getLocalizedField(opp: VolunteerOpportunity, field: string, lang: string): string {
  const suffix = lang === "ps" ? "_ps" : lang === "da" ? "_da" : "_en";
  return (opp as any)[field + suffix] || (opp as any)[field + "_en"] || "";
}

export default function VolunteerPreview() {
  const { t, i18n } = useTranslation();
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [opportunities, setOpportunities] = useState<VolunteerOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadOpportunities() {
      try {
        const { data, error: fetchErr } = await supabase
          .from("volunteer_opportunities")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(3);

        if (fetchErr) throw fetchErr;
        setOpportunities((data as VolunteerOpportunity[]) ?? []);
      } catch (err: any) {
        setError(err?.message || "Failed to load opportunities");
      } finally {
        setLoading(false);
      }
    }

    loadOpportunities();
  }, []);

  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            const children = Array.from(entry.target.children) as HTMLElement[];
            children.forEach((child) => child.classList.add("reveal-visible"));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (leftRef.current) {
      leftRef.current.classList.add("reveal-left");
      observer.observe(leftRef.current);
    }

    if (rightRef.current) {
      const children = Array.from(rightRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 130}ms`;
      });
      observer.observe(rightRef.current);
    }

    return () => observer.disconnect();
  }, [loading, opportunities]);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left */}
          <div className="lg:col-span-2" ref={leftRef}>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#3B9EF5] mb-3 block">
              {t("volunteer.tag")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B2838] leading-tight mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("volunteer.title")}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-5">{t("volunteer.body1")}</p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">{t("volunteer.body2")}</p>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1B2838] text-white rounded-full font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-[#2a3a4a] transition-colors"
            >
              <i className="ri-hand-heart-line"></i>
              {t("volunteer.cta")}
            </Link>
            <div className="mt-4">
              <a href="#" className="text-[#3B9EF5] text-sm hover:underline cursor-pointer">
                {t("volunteer.otherWays")} &rarr;
              </a>
            </div>
          </div>

          {/* Right: Cards */}
          <div className="lg:col-span-3 flex flex-col gap-4" ref={rightRef}>
            {loading ? (
              <div className="text-center py-16">
                <div className="w-10 h-10 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-gray-400 text-sm mt-3">{t("common.loading")}</p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50">
                  <i className="ri-error-warning-line text-2xl text-red-400"></i>
                </div>
                <p className="text-gray-700 font-medium mb-1">{t("volunteerPage.errorTitle")}</p>
                <p className="text-gray-400 text-sm mb-5">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i> {t("common.retry")}
                </button>
              </div>
            ) : opportunities.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-[#3B9EF5]/10">
                  <i className="ri-user-heart-line text-2xl text-[#3B9EF5]"></i>
                </div>
                <p className="text-gray-700 font-medium mb-1">{t("volunteerPage.emptyTitle")}</p>
                <p className="text-gray-400 text-sm mb-5">{t("volunteerPage.emptySubtitle")}</p>
              </div>
            ) : (
              opportunities.map((opp) => (
                <div
                  key={opp.id}
                  className="bg-white rounded-xl p-5 border border-gray-100 hover:border-[#3B9EF5]/40 transition-all duration-300 flex items-start gap-4"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#3B9EF5]/15 text-[#3B9EF5] flex-shrink-0">
                    <i className="ri-user-heart-line text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <h3 className="font-bold text-[#1B2838] text-base leading-tight">
                        {getLocalizedField(opp, "title", i18n.language)}
                      </h3>
                      <span className="text-xs bg-[#3B9EF5]/10 text-gray-400 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                        {opp.commitment}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400 text-xs mb-2">
                      <i className="ri-map-pin-line"></i>
                      <span>{opp.location}</span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                      {getLocalizedField(opp, "description", i18n.language)}
                    </p>
                  </div>
                  <Link
                    to="/volunteer"
                    className="text-[#3B9EF5] text-sm font-semibold whitespace-nowrap cursor-pointer hover:text-[#2A8DE0] flex items-center gap-1 flex-shrink-0 self-center"
                  >
                    {t("volunteer.apply")} <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}